import moment from 'moment';
import { DEFAULT_DATE_FORMAT } from 'api/models';
import { DateFieldFilterOperationType, RelativeDirection } from 'components/organisms/tasks/filters/date';
import { ZoomLevel } from 'components/pages/custom-reports/types';
import { unreachable } from 'utils/unreachable';
export var Range = /*#__PURE__*/ function(Range) {
    Range["ThisWeek"] = "this_week";
    Range["LastWeek"] = "last_week";
    Range["ThisMonth"] = "this_month";
    Range["LastMonth"] = "last_month";
    Range["Last12Months"] = "last_12_months";
    Range["Last10Years"] = "last_10_year";
    Range["ThisQuarter"] = "this_quarter";
    Range["LastQuarter"] = "last_quarter";
    Range["ThisYear"] = "this_year";
    Range["LastYear"] = "last_year";
    Range["Last30Days"] = "last_30_days";
    Range["Last60Days"] = "last_60_days";
    Range["Last90Days"] = "last_90_days";
    Range["Last180Days"] = "last_180_days";
    Range["Custom"] = "custom";
    Range["Advanced"] = "advanced";
    Range["Anytime"] = "anytime";
    Range["YearToDate"] = "year_to_date";
    Range["NextMonth"] = "next_month";
    Range["NextQuarter"] = "next_quarter";
    Range["Overall"] = "overall";
    return Range;
}({});
export const DefaultRangeOptions = [
    "this_week",
    "last_week",
    "this_month",
    "last_month",
    "this_quarter",
    "last_quarter",
    "last_90_days",
    "custom",
    "advanced",
    "anytime"
];
export const DAY_GRANULARITY_ALLOWED_RANGES = [
    "this_week",
    "last_week",
    "this_month",
    "last_month",
    "last_30_days",
    "custom"
];
export const isRangeAllowedForZoomLevel = (range, zoomLevel)=>{
    if (zoomLevel !== ZoomLevel.Daily) {
        return true;
    }
    if (!range) {
        return true;
    }
    if (!DAY_GRANULARITY_ALLOWED_RANGES.includes(range.type)) {
        return false;
    }
    // Custom ranges must also be ≤30 days; a pre-saved wide custom range is forbidden.
    if (range.type === "custom") {
        const { startDate, endDate } = range;
        if (!startDate || !endDate) {
            return true;
        }
        const start = moment(startDate);
        const end = moment(endDate);
        if (!start.isValid() || !end.isValid()) {
            return true;
        }
        return end.diff(start, 'days') <= 31;
    }
    return true;
};
export const getRangeObject = (range, startDate, endDate)=>{
    switch(range){
        case "this_week":
        case "last_week":
        case "this_month":
        case "last_month":
        case "this_quarter":
        case "last_quarter":
        case "last_30_days":
        case "last_60_days":
        case "last_90_days":
        case "last_180_days":
        case "last_12_months":
        case "last_10_year":
        case "this_year":
        case "last_year":
        case "year_to_date":
        case "overall":
        case "anytime":
        case "next_month":
        case "next_quarter":
            return {
                type: range
            };
        case "custom":
        case "advanced":
            return startDate && endDate ? {
                type: range,
                endDate,
                startDate
            } : unreachable(range);
        default:
            return unreachable(range);
    }
};
/**
 * @deprecated Use useCalendarEngine instead
 */ export const getValueFromDateRange = (range)=>{
    switch(range.type){
        case "this_week":
            return [
                moment().startOf('week'),
                moment().endOf('week')
            ];
        case "last_week":
            return [
                moment().subtract(1, 'week').startOf('week'),
                moment().subtract(1, 'week').endOf('week')
            ];
        case "this_month":
            return [
                moment().startOf('month'),
                moment().endOf('month')
            ];
        case "last_month":
            return [
                moment().subtract(1, 'month').startOf('month'),
                moment().subtract(1, 'month').endOf('month')
            ];
        case "next_month":
            return [
                moment().add(1, 'month').startOf('month'),
                moment().add(1, 'month').endOf('month')
            ];
        case "this_quarter":
            return [
                moment().startOf('quarter'),
                moment().endOf('quarter')
            ];
        case "last_quarter":
            return [
                moment().subtract(1, 'quarter').startOf('quarter'),
                moment().subtract(1, 'quarter').endOf('quarter')
            ];
        case "next_quarter":
            return [
                moment().add(1, 'quarter').startOf('quarter'),
                moment().add(1, 'quarter').endOf('quarter')
            ];
        case "last_30_days":
            return [
                moment().subtract(30, 'days').startOf('day'),
                moment().endOf('day')
            ];
        case "last_60_days":
            return [
                moment().subtract(60, 'days').startOf('day'),
                moment().endOf('day')
            ];
        case "last_90_days":
            return [
                moment().subtract(90, 'days').startOf('day'),
                moment().endOf('day')
            ];
        case "last_180_days":
            return [
                moment().subtract(180, 'days').startOf('day'),
                moment().endOf('day')
            ];
        case "last_12_months":
            return [
                moment().subtract(365, 'days').startOf('day'),
                moment().endOf('day')
            ];
        case "last_10_year":
            return [
                moment().subtract(10, 'years').startOf('day'),
                moment().endOf('day')
            ];
        case "this_year":
            return [
                moment().startOf('year'),
                moment().endOf('year')
            ];
        case "last_year":
            return [
                moment().startOf('year').subtract(1, 'year'),
                moment().endOf('year').subtract(1, 'year')
            ];
        case "year_to_date":
            return [
                moment().startOf('year'),
                moment().endOf('day')
            ];
        case "anytime":
            return [
                moment(0),
                moment()
            ];
        case "overall":
            /**
       * In order to not break existing implementations of DateRange logic,
       * setting both startDate and endDate as moment(0) for Overall range.
       * Need to handle appropriately at the consuming end
       */ return [
                moment(0),
                moment(0)
            ];
        case "custom":
        case "advanced":
            return [
                range.startDate,
                range.endDate
            ];
        default:
            return unreachable(range);
    }
};
export const getCustomRangeObject = (startDate, endDate)=>getRangeObject("custom", startDate, endDate);
/**
 * @deprecated Use useCalendarEngine instead
 */ export const getRangeObjectForRelativeTypes = (type, value, direction)=>{
    if (direction === RelativeDirection.Ago) {
        switch(type){
            case DateFieldFilterOperationType.IsMoreThan:
                return {
                    type: "advanced",
                    startDate: moment(0),
                    endDate: moment().subtract(value, 'days')
                };
            case DateFieldFilterOperationType.IsLessThan:
                return {
                    type: "advanced",
                    endDate: moment(),
                    startDate: moment().subtract(value, 'days')
                };
            case DateFieldFilterOperationType.IsExactly:
                return {
                    type: "advanced",
                    startDate: moment().subtract(value, 'days'),
                    endDate: moment().subtract(value, 'days')
                };
            default:
                return unreachable(type);
        }
    } else if (direction === RelativeDirection.FromNow) {
        switch(type){
            case DateFieldFilterOperationType.IsMoreThan:
                return {
                    type: "advanced",
                    endDate: moment().add(100, 'years'),
                    startDate: moment().add(value, 'days')
                };
            case DateFieldFilterOperationType.IsLessThan:
                return {
                    type: "advanced",
                    startDate: moment(),
                    endDate: value > 0 ? moment().add(value, 'days') : moment()
                };
            case DateFieldFilterOperationType.IsExactly:
                return {
                    type: "advanced",
                    startDate: moment().add(value, 'days'),
                    endDate: moment().add(value, 'days')
                };
            default:
                return unreachable(type);
        }
    } else {
        return unreachable(direction);
    }
};
/**
 * @deprecated Use useCalendarEngine instead
 */ export const getRangeObjectForAbsoluteTypes = (type, value)=>{
    switch(type){
        case DateFieldFilterOperationType.IsAfter:
            return {
                type: "advanced",
                startDate: moment.utc(`${value.format('YYYY-MM-DD')}T00:00:00Z`),
                endDate: moment().utc()
            };
        case DateFieldFilterOperationType.IsBefore:
            return {
                type: "advanced",
                endDate: moment.utc(`${value.format('YYYY-MM-DD')}T00:00:00Z`),
                startDate: moment(0).utc()
            };
        case DateFieldFilterOperationType.IsOn:
            return {
                type: "advanced",
                endDate: moment.utc(`${value.format('YYYY-MM-DD')}T00:00:00Z`),
                startDate: moment.utc(`${value.format('YYYY-MM-DD')}T00:00:00Z`)
            };
        default:
            return unreachable(type);
    }
};
/**
 * @deprecated Use useCalendarEngine instead
 */ export const getPreviousRangeForCustom = (range)=>{
    const startDate = moment(range.startDate);
    const endDate = moment(range.endDate);
    const diff = endDate.diff(startDate, 'days');
    return getCustomRangeObject(startDate.subtract(diff + 1, 'days').startOf('day'), startDate.subtract(1, 'days').startOf('day'));
};
/**
 * @deprecated Use useCalendarEngine instead
 */ export const getPreviousRange = (range)=>{
    const previousRangeMap = {
        ["this_week"]: getCustomRangeObject(moment().subtract(1, 'week').startOf('week'), moment().subtract(1, 'week').endOf('week')),
        ["last_week"]: getCustomRangeObject(moment().subtract(2, 'week').startOf('week'), moment().subtract(2, 'week').endOf('week')),
        ["this_month"]: getCustomRangeObject(moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')),
        ["last_month"]: getCustomRangeObject(moment().subtract(2, 'month').startOf('month'), moment().subtract(2, 'month').endOf('month')),
        ["this_quarter"]: getCustomRangeObject(moment().subtract(1, 'quarter').startOf('quarter'), moment().subtract(1, 'quarter').endOf('quarter')),
        ["last_quarter"]: getCustomRangeObject(moment().subtract(2, 'quarter').startOf('quarter'), moment().subtract(2, 'quarter').endOf('quarter')),
        ["last_30_days"]: getCustomRangeObject(moment().subtract(60, 'days').startOf('day'), moment().subtract(30, 'days').startOf('day')),
        ["last_60_days"]: getCustomRangeObject(moment().subtract(120, 'days').startOf('day'), moment().subtract(60, 'days').startOf('day')),
        ["last_90_days"]: getCustomRangeObject(moment().subtract(180, 'days').startOf('day'), moment().subtract(90, 'days').startOf('day')),
        ["last_180_days"]: getCustomRangeObject(moment().subtract(360, 'days').startOf('day'), moment().subtract(180, 'days').startOf('day')),
        ["this_year"]: getCustomRangeObject(moment().subtract(1, 'year').startOf('year'), moment().subtract(1, 'year').endOf('year')),
        ["year_to_date"]: getCustomRangeObject(moment().subtract(1, 'year').endOf('year').subtract(moment().endOf('day').diff(moment().startOf('year'), 'days'), 'days'), moment().subtract(1, 'year').endOf('year')),
        ["anytime"]: [
            moment(0),
            moment()
        ],
        ["overall"]: [
            moment(0),
            moment(0)
        ],
        ["custom"]: getPreviousRangeForCustom(range)
    };
    const result = previousRangeMap[range.type];
    return result || unreachable(range);
};
/**
 * @deprecated Use useCalendarEngine instead
 */ export const getFormattedValueFromDateRange = (range, format = DEFAULT_DATE_FORMAT)=>getValueFromDateRange(range).map((x)=>x.format(format));
